/**
 * @file ArchiveService — discovers past competitions from the archive index and
 * loads a specific archived week. The index (archive/index.json on Backblaze) is
 * the single source of truth; nothing about past weeks is hardcoded and no
 * metadata is duplicated into the index — per-week details come from lazily
 * fetching each (cached) manifest.
 */

import {
  ARCHIVE_INDEX_URL,
  resolveArchiveManifestUrl,
} from '../config/scrWeeklyConfig';
import { getJson } from './httpClient';
import { getManifest } from './manifestCache';

/**
 * @typedef {import('../models/archive').ArchiveIndexEntry} ArchiveIndexEntry
 * @typedef {import('../models/manifest').Competition} Competition
 */

/** Cache the (small) index for the session. @type {ArchiveIndexEntry[]|null} */
let indexCache = null;
/** @type {Promise<ArchiveIndexEntry[]>|null} */
let indexInFlight = null;

/**
 * Normalize + sort raw index entries (newest first).
 * @param {*} raw
 * @returns {ArchiveIndexEntry[]}
 */
function normalizeIndex(raw) {
  const entries = Array.isArray(raw && raw.entries) ? raw.entries : [];
  return entries
    .filter((e) => e && (e.id || e.manifestUrl))
    .map((e) => ({
      id: e.id || '',
      season: Number(e.season),
      week: Number(e.week),
      manifestUrl: e.manifestUrl || '',
    }))
    .sort((a, b) =>
      a.season !== b.season ? b.season - a.season : b.week - a.week
    );
}

export const ArchiveService = {
  /**
   * Fetch the archive index (cached for the session).
   * @returns {Promise<ArchiveIndexEntry[]>}
   */
  getArchiveIndex() {
    if (indexCache) return Promise.resolve(indexCache);
    if (indexInFlight) return indexInFlight;

    indexInFlight = getJson(ARCHIVE_INDEX_URL)
      .then((raw) => {
        indexCache = normalizeIndex(raw);
        indexInFlight = null;
        return indexCache;
      })
      .catch((err) => {
        indexInFlight = null;
        throw err;
      });

    return indexInFlight;
  },

  /**
   * The absolute manifest URL for an archive entry.
   * @param {ArchiveIndexEntry} entry
   * @returns {string}
   */
  manifestUrlFor(entry) {
    return resolveArchiveManifestUrl(entry);
  },

  /**
   * Lazily load one archived competition's full manifest (cached).
   * @param {ArchiveIndexEntry} entry
   * @returns {Promise<Competition>}
   */
  getCompetitionForEntry(entry) {
    return getManifest(resolveArchiveManifestUrl(entry));
  },

  /**
   * Look up an archived competition by its id (e.g. "s1-w29"), resolving via the
   * index so a missing id surfaces cleanly.
   * @param {string} id
   * @returns {Promise<{ entry: ArchiveIndexEntry, manifestUrl: string, competition: Competition }>}
   */
  async getById(id) {
    const index = await this.getArchiveIndex();
    const entry = index.find((e) => e.id === id);
    if (!entry) {
      const err = new Error(`Archived challenge "${id}" is not in the index.`);
      err.code = 'NOT_FOUND';
      throw err;
    }
    const manifestUrl = resolveArchiveManifestUrl(entry);
    const competition = await getManifest(manifestUrl);
    return { entry, manifestUrl, competition };
  },
};
